"use client";

import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ClockIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

export interface RentalCarTimesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  searchBar?: boolean;
  dropOffLocationType?: boolean;
  // Variant control: show only pickup time or dropoff time in the button UI.
  variant?: "pickup" | "dropoff";
  // Hide inline label text when embedding inside a grouped field.
  hideLabel?: boolean;
  // Extra classes to style the visible segment box (e.g., rounded overrides)
  segmentClassName?: string;
  // Placeholder text for pickup and dropoff times
  pickupPlaceholder?: string;
  dropoffPlaceholder?: string;
}

const timeSlots = Array.from({ length: 24 }, (_, h) => [
  `${h.toString().padStart(2, "0")}:00`,
  `${h.toString().padStart(2, "0")}:30`,
]).flat();

const RentalCarTimesRangeInput: FC<RentalCarTimesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  hasButtonSubmit = true,
  searchBar,
  variant,
  hideLabel,
  segmentClassName,
  pickupPlaceholder,
  dropoffPlaceholder,
}) => {
  const {
    startDate,

    loadingAvailableFleet,
    setStartDate,
    setEndDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    callAvailableFleetAPI,
    partiallyUnavailableTimes,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    ourCompany,
    hasSelectedStartTime,
    setHasSelectedStartTime,
    hasSelectedEndTime,
    setHasSelectedEndTime,
  } = useData();

  const location = useLocation();
  const navigate = useNavigate();

  const shouldBlockTime =
    location.pathname === "/listing-car-detail" ||
    location.pathname === "/car-listing-detail" ||
    location.pathname.startsWith("/book/");

  useEffect(() => {
    validateEndTime();
  }, [startDate]);

  const validateEndTime = () => {
    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTimeObj = new Date();
      startTimeObj.setHours(startHour, startMinute);

      const endTimeObj = new Date();
      endTimeObj.setHours(endHour, endMinute);
    }
  };

  const formatTime = (time: string) => {
    if (time === "00:00") return "Midnight";
    return moment(time, "HH:mm").format("h:mm A");
  };

  const renderSearchInput = () => {
    const getPlaceholderText = (isPickup: boolean) => {
      const defaultPickup = "Pickup Time";
      const defaultDropoff = "Dropoff Time";
      const text = isPickup
        ? pickupPlaceholder || defaultPickup
        : dropoffPlaceholder || defaultDropoff;

      return <span>{text}</span>;
    };

    return (
      <>
        <div className="flex-grow text-left ">
          {!hideLabel && (
            <span className="block mt-1 text-xs text-neutral-400 leading-none font-light">
              {variant === "pickup"
                ? "Pickup Time"
                : variant === "dropoff"
                  ? "Return Time"
                  : "Pickup & Return Time"}
            </span>
          )}
          <div
            className={`${
              hideLabel ? "mt-0" : "mt-1"
            } flex items-center space-x-2`}
          >
            {(variant === undefined || variant === "pickup") && (
              <div
                className={`flex items-center justify-between bg-gradient-to-b from-[#333333] to-[#222222] border border-[#FFFFFF1A] rounded-md px-3 py-2 w-full lg:w-auto md:flex-none md:max-w-none lg:min-w-[100px] lg:max-w-[150px] ${
                  segmentClassName ?? ""
                }`}
              >
                <span
                  className={`text-sm font-normal truncate ${
                    !hasSelectedStartTime ? "text-[#E5E7EB]" : ""
                  }`}
                >
                  {hasSelectedStartTime
                    ? formatTime(startTime)
                    : getPlaceholderText(true)}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
              </div>
            )}
            {(variant === undefined || variant === "dropoff") && (
              <div
                className={`flex items-center justify-between bg-gradient-to-b from-[#333333] to-[#222222] border border-[#FFFFFF1A] rounded-md px-3 py-2 w-full lg:w-auto md:flex-none md:max-w-none lg:min-w-[100px] lg:max-w-[150px] ${
                  segmentClassName ?? ""
                }`}
              >
                <span
                  className={`text-sm font-normal truncate ${
                    !hasSelectedEndTime ? "text-[#E5E7EB]" : ""
                  }`}
                >
                  {hasSelectedEndTime
                    ? formatTime(endTime)
                    : getPlaceholderText(false)}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.7306 21.5041C16.589 21.5041 20.5276 17.5656 20.5276 12.7071C20.5276 7.8487 16.589 3.91016 11.7306 3.91016C6.87214 3.91016 2.93359 7.8487 2.93359 12.7071C2.93359 17.5656 6.87214 21.5041 11.7306 21.5041Z"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
            />
            <path
              d="M4.88848 18.5718L2.93359 20.5267M18.5727 18.5718L20.5276 20.5267"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M18.5726 3.48947L19.1546 3.19843C19.9809 2.78534 20.2912 2.83262 20.9481 3.48947C21.6049 4.14631 21.6522 4.45669 21.239 5.28288L20.9481 5.86495M4.88838 3.48947L4.30632 3.19843C3.48012 2.78534 3.16975 2.83262 2.51291 3.48947C1.85605 4.14631 1.80878 4.45669 2.22187 5.28288L2.51291 5.86495"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
            />
            <path
              d="M11.7305 9.28564V13.1954L13.6854 15.1503"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M11.7305 3.42124V1.95508"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.77637 1.95508H13.6861"
              stroke="white"
              stroke-width="1.95488"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div className="flex-grow text-left">
          <span className="block text-sm font-semibold truncate break-words text-white">
            {hasSelectedStartTime
              ? `${startTime === "00:00" ? "Midnight" : formatTime(startTime)}`
              : pickupPlaceholder || "Pickup Time"}{" "}
            {pickupPlaceholder === "Select" ? "" : "-"}{" "}
            {hasSelectedEndTime
              ? `${endTime === "00:00" ? "Midnight" : formatTime(endTime)}`
              : dropoffPlaceholder || "Return Time"}
          </span>
          <span className="block mt-1 text-xs text-white leading-none">
            {"Pickup & Return Time"}
          </span>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (startDate && endDate) {
      const startDateTime = moment(
        `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();

      const endDateTime = moment(
        `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();

      setStartDate(startDateTime);
      setEndDate(endDateTime);
    }
  }, [startTime, endTime]);

  // Define time range type
  type TimeRange = {
    from: string; // HH:mm:ss
    to: string; // HH:mm:ss
  };

  type PartialUnavailability = {
    [date: string]: TimeRange[];
  };

  const getPartialUnavailableRanges = (
    date: Date | null | undefined,
  ): TimeRange[] => {
    if (!date) return [];
    const m = moment(date);
    if (!m.isValid()) return [];
    const dateKey = m.format("YYYY-MM-DD");
    const ranges = partiallyUnavailableTimes[dateKey];
    return Array.isArray(ranges) ? ranges : [];
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false; // or `true` if you want to disable until a date is picked
    const ranges = getPartialUnavailableRanges(date);
    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Check if time is outside operational hours
  const isOutsideOperationalHours = (time: string): boolean => {
    if (!pickupLocation) return false;

    const { operational_start_time, operational_end_time } = pickupLocation;

    // If operational hours are null/undefined, don't block any times
    if (!operational_start_time || !operational_end_time) return false;

    const timeMoment = moment(time, "HH:mm");
    const startMoment = moment(operational_start_time, "HH:mm:ss");
    const endMoment = moment(operational_end_time, "HH:mm:ss");

    // Check if operational hours span midnight
    if (endMoment.isBefore(startMoment)) {
      // e.g., 22:00 to 06:00 (overnight)
      return timeMoment.isBefore(startMoment) && timeMoment.isAfter(endMoment);
    } else {
      // Normal case: e.g., 09:00 to 18:00
      return timeMoment.isBefore(startMoment) || timeMoment.isAfter(endMoment);
    }
  };

  // Find the next available time from a given slot in your 30-min list
  const getNextAvailableTime = (
    date: Date | null | undefined,
    time: string,
    timeSlots: string[],
  ): string => {
    if (!date) return time;

    for (let i = timeSlots.indexOf(time); i < timeSlots.length; i++) {
      if (
        !isTimeBlocked(date, timeSlots[i]) &&
        !isOutsideOperationalHours(timeSlots[i])
      )
        return timeSlots[i];
    }

    for (let i = 0; i < timeSlots.length; i++) {
      if (
        !isTimeBlocked(date, timeSlots[i]) &&
        !isOutsideOperationalHours(timeSlots[i])
      )
        return timeSlots[i];
    }

    // Default to first slot if all are blocked
    return timeSlots[0];
  };

  const isTimePast = (time: string): boolean => {
    const now = moment();
    const advanceNoticeHours = ourCompany?.default_advance_notice || 0;
    const timeWithAdvanceNotice = now.clone().add(advanceNoticeHours, "hours");
    const timeMoment = moment(time, "HH:mm");
    return timeMoment.isBefore(timeWithAdvanceNotice);
  };

  useEffect(() => {
    if (startDate && startTime && isTimeBlocked(startDate, startTime)) {
      setStartTime(getNextAvailableTime(startDate, startTime, timeSlots));
    }
    if (endDate && endTime && isTimeBlocked(endDate, endTime)) {
      setEndTime(getNextAvailableTime(endDate, endTime, timeSlots));
    }
  }, [startDate, endDate]);

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative  flex ${className}`}
      >
        {({ open }) => (
          <>
            <div
              className={`flex-1 z-10 flex items-center focus:outline-none ${
                open ? "" : ""
              }`}
            >
              <PopoverButton
                className={`flex flex-1 relative items-center space-x-3 focus:outline-none ${
                  (location.pathname === "/car-listing-detail" ||
                    location.pathname === "/listing-car-detail" ||
                    location.pathname.startsWith("/book/")) &&
                  !searchBar
                    ? "p-3"
                    : "p-0 border-l-0"
                }`}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                {(location.pathname === "/car-listing-detail" ||
                  location.pathname === "/listing-car-detail" ||
                  location.pathname.startsWith("/book/")) &&
                !searchBar
                  ? renderInput()
                  : renderSearchInput()}
              </PopoverButton>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                className={`absolute z-40 mt-3 top-full w-screen max-w-xs lg:max-w-l ${
                  !searchBar
                    ? "left-1/2 -translate-x-1/2 md:left-1/2 md:-translate-x-1/2"
                    : "right-full translate-x-1/2"
                } px-2 overflow-hidden`}
              >
                <div className="p-4 space-x-2 overflow-visible rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-[#444444] border border-[#FFFFFF1A]">
                  <div
                    className={`grid ${
                      !variant ? "grid-cols-2" : "grid-cols-1"
                    }`}
                  >
                    {(variant === undefined || variant === "pickup") && (
                      <div>
                        <h3 className="text-lg font-medium flex justify-center">
                          Pickup Time
                        </h3>

                        <div className="overflow-y-auto h-48">
                          {timeSlots.map((time) => {
                            const isTimeBlocked_ = shouldBlockTime
                              ? isTimeBlocked(startDate ?? null, time)
                              : false;
                            const isPast =
                              startDate &&
                              moment(startDate).isSame(moment(), "day") &&
                              isTimePast(time);
                            const outsideHours =
                              isOutsideOperationalHours(time);
                            const isUnavailable =
                              isTimeBlocked_ || isPast || outsideHours;

                            return (
                              <div
                                key={time}
                                className={`cursor-pointer text-center py-2 px-4 mb-1 rounded-none  hover:bg-primary-100 transition ${
                                  startTime === time ? "bg-primary-200 " : ""
                                } ${
                                  isUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through pointer-events-none"
                                    : ""
                                }`}
                                onClick={() => {
                                  setStartTime(time);
                                  setHasSelectedStartTime(true);
                                }}
                              >
                                {formatTime(time)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(variant === undefined || variant === "dropoff") && (
                      <div>
                        <h3 className="text-lg font-medium flex justify-center">
                          Return Time
                        </h3>
                        <div className="overflow-y-auto h-48">
                          {timeSlots.map((time) => {
                            const isTimeBlocked_ = shouldBlockTime
                              ? isTimeBlocked(endDate ?? null, time)
                              : false;
                            const isPast =
                              endDate &&
                              moment(endDate).isSame(moment(), "day") &&
                              isTimePast(time);
                            const outsideHours =
                              isOutsideOperationalHours(time);
                            const isUnavailable =
                              isTimeBlocked_ || isPast || outsideHours;

                            return (
                              <div
                                key={time}
                                className={`cursor-pointer text-center py-2 px-4 hover:bg-primary-200  rounded-none ${
                                  endTime === time ? "bg-primary-200 " : ""
                                } ${
                                  isUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through pointer-events-none"
                                    : ""
                                }`}
                                onClick={() => {
                                  setEndTime(time);
                                  setHasSelectedEndTime(true);
                                }}
                              >
                                {formatTime(time)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <PopoverButton className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-white font-semibold py-3 rounded-xl transition">
                    Select
                  </PopoverButton>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </>
  );
};

export default RentalCarTimesRangeInput;
